import React, { useState, useEffect } from 'react';
import { X, AlertCircle } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../../services/api';
import type { Leaseholder } from '../../../../types/leaseholder';

interface AddSubLeaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Predefined options for structured form fields with database codes
// These values match the method_id values in the rent_method table
const LEASE_METHODS = [
  { value: 'C', label: 'Non-Retail Commercial (Non-Government)' },
  { value: 'CG', label: 'Non-Retail Commercial (Government)' },
  { value: 'CH', label: 'Community housing (peppercorn)' },
  { value: 'PS', label: 'Public Space' },
  { value: 'R3', label: 'Retail - (Commercial) (Under 3 million)' },
  { value: 'R5', label: 'Retail - (Commercial) (Gross income over 5 mil)' },
  { value: 'RG', label: 'Residential (Government)' },
  { value: 'RN', label: 'Residential (Non-Government)' },
  { value: 'SD', label: 'Short-term Development Sublease' },
  { value: 'U', label: 'Undeveloped (large parcels)' }
];

// The 'value' is the 1-character code stored in the database (lease_type field is varchar(1))
// Note: We need to verify these values against the actual database table
const LEASE_TYPES = [
  { value: 'C', label: 'Commercial' },
  { value: 'R', label: 'Residential' },
  { value: 'I', label: 'Industrial' },
  { value: 'M', label: 'Mixed Use' },
  { value: 'O', label: 'Other' }
];

const REVIEW_INTERVALS = [
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Annually', label: 'Annually' },
  { value: 'When required', label: 'When Required' },
  { value: 'N/A', label: 'Not Applicable' }
];

const LEASE_DEALINGS = [
  { value: 'New', label: 'New Lease' },
  { value: 'Renewal', label: 'Renewal' },
  { value: 'Transfer', label: 'Transfer' },
  { value: 'Amendment', label: 'Amendment' }
];

export default function AddSubLeaseModal({ isOpen, onClose, onSuccess }: AddSubLeaseModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [lots, setLots] = useState<Array<{
    lot_id: string;
    lot_avail: boolean;
    community_name: string | null;
  }>>([]);
  const [formData, setFormData] = useState({
    lotId: '',
    holderId: '',
    holderName: '', // Keep for backward compatibility
    startDate: '',
    endDate: '',
    permittedUse: '',
    rentalAmount: '',
    reviewInterval: '',
    leaseMethod: '',
    leaseType: '',
    leaseDealing: '',
    leaseFurthTerm: '',
    leaseDealDoc: ''
  });

  // Fetch leaseholders with React Query
  const {
    data: leaseholders,
    isLoading: isLoadingLeaseholders,
    error: leaseholdersError
  } = useQuery<Leaseholder[]>({
    queryKey: ['leaseholders'],
    queryFn: api.leaseholders.getAll
  });

  // Debug logging for leaseholders
  useEffect(() => {
    if (leaseholdersError) {
      console.error('Error fetching leaseholders:', leaseholdersError);
    }

    if (leaseholders) {
      console.log('Leaseholders data loaded:', leaseholders);
      console.log('Number of leaseholders:', leaseholders.length);

      if (leaseholders.length > 0) {
        console.log('First leaseholder:', leaseholders[0]);
      }
    }
  }, [leaseholders, leaseholdersError]);

  // Fetch lots data when modal opens
  useEffect(() => {
    if (isOpen) {
      const fetchLots = async () => {
        try {
          const { data, error } = await supabase
            .from('lot_lease_summary')
            .select('lot_id, lot_avail, community_name')
            .order('lot_id');

          if (error) {
            console.error('Error fetching lots:', error);
            throw error;
          }
          setLots(data || []);
        } catch (err) {
          console.error('Error fetching lots:', err);
        }
      };

      fetchLots();
    }
  }, [isOpen]);

  // Reset form and errors when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        lotId: '',
        holderId: '',
        holderName: '',
        startDate: '',
        endDate: '',
        permittedUse: '',
        rentalAmount: '',
        reviewInterval: '',
        leaseMethod: '',
        leaseType: '',
        leaseDealing: '',
        leaseFurthTerm: '',
        leaseDealDoc: ''
      });
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen]);

  // Fetch the permitted use value when a lot is selected
  const fetchPermittedUse = async (lotId: string) => {
    console.log('Fetching permitted use for lot ID:', lotId);

    try {
      // First try to get from lease_lots table
      const { data: leaseLotsData, error: leaseLotsError } = await supabase
        .from('lease_lots')
        .select('leaselot_use')
        .eq('leaselot_lot', lotId)
        .maybeSingle();

      if (leaseLotsError) {
        console.error('Error fetching from lease_lots:', leaseLotsError);
      } else {
        console.log('lease_lots query result:', leaseLotsData);
      }

      // If we found data in lease_lots, use it
      if (leaseLotsData && leaseLotsData.leaselot_use) {
        console.log('Found permitted use in lease_lots:', leaseLotsData.leaselot_use);
        setFormData(prev => ({ ...prev, permittedUse: leaseLotsData.leaselot_use }));
        return;
      }

      // If not found in lease_lots, try land_lots table
      const { data: landLotsData, error: landLotsError } = await supabase
        .from('land_lots')
        .select('lot_perm_use')
        .eq('lot_id', lotId)
        .maybeSingle();

      if (landLotsError) {
        console.error('Error fetching from land_lots:', landLotsError);
      } else {
        console.log('land_lots query result:', landLotsData);
      }

      // If we found data in land_lots, use it
      if (landLotsData && landLotsData.lot_perm_use) {
        console.log('Found permitted use in land_lots:', landLotsData.lot_perm_use);
        setFormData(prev => ({ ...prev, permittedUse: landLotsData.lot_perm_use }));
        return;
      }

      // If we still don't have data, try lot_lease_summary view
      const { data: summaryData, error: summaryError } = await supabase
        .from('lot_lease_summary')
        .select('lot_perm_use')
        .eq('lot_id', lotId)
        .maybeSingle();

      if (summaryError) {
        console.error('Error fetching from lot_lease_summary:', summaryError);
      } else {
        console.log('lot_lease_summary query result:', summaryData);
      }

      // If we found data in lot_lease_summary, use it
      if (summaryData && summaryData.lot_perm_use) {
        console.log('Found permitted use in lot_lease_summary:', summaryData.lot_perm_use);
        setFormData(prev => ({ ...prev, permittedUse: summaryData.lot_perm_use }));
        return;
      }

      console.log('No permitted use found for lot ID:', lotId);

    } catch (err) {
      console.error('Error fetching permitted use:', err);
    }
  };

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.lotId) {
      errors.lotId = 'Lot ID is required';
    }

    // Add validation for holderId to ensure a leaseholder is selected if required
    // Note: This is commented out because it's not clear if a leaseholder is required
    // Uncomment this if a leaseholder selection is mandatory
    /*
    if (!formData.holderId) {
      errors.holderName = 'Please select a leaseholder';
    }
    */

    if (formData.startDate && formData.endDate && new Date(formData.startDate) > new Date(formData.endDate)) {
      errors.endDate = 'End date must be after start date';
    }

    if (formData.leaseFurthTerm && formData.leaseFurthTerm.length > 30) {
      errors.leaseFurthTerm = 'Further term cannot exceed 30 characters';
    }

    // Deal document has no explicit character limit now.

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate form before submission
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Log the form data for debugging
      console.log('Submitting form data:', formData);

      // Use direct table operations instead of RPC function
      // This ensures proper use of the database schema and foreign key relationships

      // Get the selected lease method and type
      const selectedLeaseMethod = LEASE_METHODS.find(method => method.value === formData.leaseMethod);
      const selectedLeaseType = LEASE_TYPES.find(type => type.value === formData.leaseType);

      // Log for debugging
      console.log('Selected lease method:', formData.leaseMethod);
      console.log('Selected lease type:', formData.leaseType);

      // Get the maximum lease_id value to ensure we use a unique ID
      const { data: maxIdData, error: maxIdError } = await supabase
        .from('subleases')
        .select('lease_id')
        .order('lease_id', { ascending: false })
        .limit(1)
        .single();

      if (maxIdError && maxIdError.code !== 'PGRST116') { // PGRST116 is "no rows returned" which is fine
        console.error('Error getting max lease_id:', maxIdError);
        throw new Error(`Failed to get max lease_id: ${maxIdError.message}`);
      }

      // Calculate the next lease_id (max + 1, or 1 if no records exist)
      const nextLeaseId = maxIdData ? maxIdData.lease_id + 1 : 1;
      console.log('Using next lease_id:', nextLeaseId);

      // 1. First create the sublease record with an explicit lease_id
      let leaseData;
      let leaseError;

      // Try to insert with the next available ID
      const initialInsert = await supabase
        .from('subleases')
        .insert([{
          lease_id: nextLeaseId, // Explicitly set the lease_id
          lease_holder: formData.holderId ? parseInt(formData.holderId) : null,
          lease_start: formData.startDate || null,
          lease_end: formData.endDate || null,
          lease_method: formData.leaseMethod || null, // Use the value directly
          lease_type: formData.leaseType || null, // Use the value directly
          lease_rent_freq: formData.reviewInterval || null,
          lease_dealing: formData.leaseDealing || null,
          lease_furth_term: formData.leaseFurthTerm || null,
          lease_deal_doc: formData.leaseDealDoc || null
        }])
        .select('lease_id')
        .single();

      leaseData = initialInsert.data;
      leaseError = initialInsert.error;

      // If we get a primary key violation, try with an incremented ID
      if (leaseError && leaseError.message.includes('subleases_pkey')) {
        console.warn('Primary key conflict, retrying with incremented ID');

        const retryInsert = await supabase
          .from('subleases')
          .insert([{
            lease_id: nextLeaseId + 1, // Try with next + 1
            lease_holder: formData.holderId ? parseInt(formData.holderId) : null,
            lease_start: formData.startDate || null,
            lease_end: formData.endDate || null,
            lease_method: formData.leaseMethod || null, // Use the value directly
            lease_type: formData.leaseType || null, // Use the value directly
            lease_rent_freq: formData.reviewInterval || null,
            lease_dealing: formData.leaseDealing || null,
            lease_furth_term: formData.leaseFurthTerm || null,
            lease_deal_doc: formData.leaseDealDoc || null
          }])
          .select('lease_id')
          .single();

        leaseData = retryInsert.data;
        leaseError = retryInsert.error;
      }

      // If we still have an error, throw it
      if (leaseError) {
        console.error('Error creating sublease record:', leaseError);
        throw new Error(`Failed to create sublease record: ${leaseError.message}`);
      }

      if (!leaseData) {
        throw new Error('Failed to create sublease record: No data returned');
      }

      const leaseId = leaseData.lease_id;

      // 2. Check if the land_lot already exists
      const { data: existingLot, error: checkLotError } = await supabase
        .from('land_lots')
        .select('lot_id')
        .eq('lot_id', formData.lotId)
        .maybeSingle();

      if (checkLotError) {
        console.error('Error checking if lot exists:', checkLotError);
        throw new Error(`Failed to check if lot exists: ${checkLotError.message}`);
      }

      // 3. If the lot doesn't exist, create it
      if (!existingLot) {
        const { error: insertLotError } = await supabase
          .from('land_lots')
          .insert([{
            lot_id: formData.lotId,
            holder_id: formData.holderId ? parseInt(formData.holderId) : null,
            holder_name: formData.holderName || null, // Keep for backward compatibility
            lot_perm_use: formData.permittedUse || null,
          }]);

        if (insertLotError) {
          console.error('Error creating land lot:', insertLotError);
          throw new Error(`Failed to create land lot: ${insertLotError.message}`);
        }
      }

      // 4. Create the relationship in lease_lots table
      const { error: leaseLotsError } = await supabase
        .from('lease_lots')
        .insert([{
          leaselot_lease: leaseId,
          leaselot_lot: formData.lotId,
          leaselot_use: formData.permittedUse || null
        }]);

      if (leaseLotsError) {
        console.error('Error creating lease-lot relationship:', leaseLotsError);
        throw new Error(`Failed to create lease-lot relationship: ${leaseLotsError.message}`);
      }

      // 5. Create a record in the rental_contract table if rental amount is provided
      if (formData.rentalAmount && formData.rentalAmount.trim() !== '') {
        console.log('Creating rental contract with amount:', formData.rentalAmount);

        try {
          const { data: insertData, error: rentalError } = await supabase
            .from('rental_contract')
            .insert([{
              // rental_trans_id will be auto-generated by the sequence
              rental_lease: leaseId,
              rental_amount: parseFloat(formData.rentalAmount),
              rental_start: formData.startDate || null
            }])
            .select();

          if (rentalError) {
            console.error('Error creating rental contract:', rentalError);
            throw new Error(`Failed to create rental contract: ${rentalError.message}`);
          }

          console.log('Successfully created rental contract for lease ID:', leaseId);
          console.log('Rental contract data:', insertData);

          // Verify the rental contract was created
          const { data: verifyData, error: verifyError } = await supabase
            .from('rental_contract')
            .select('*')
            .eq('rental_lease', leaseId);

          if (verifyError) {
            console.error('Error verifying rental contract:', verifyError);
          } else {
            console.log('Verification of rental contract:', verifyData);
          }
        } catch (rentalErr) {
          // Log the error but don't fail the whole transaction
          // This way, the sublease is still created even if the rental record fails
          console.error('Error creating rental contract:', rentalErr);
          setError(prev => prev ? `${prev}. Note: Rental amount was not saved properly.` : 'Sublease created, but rental amount was not saved properly.');
        }
      } else {
        console.log('No rental amount provided, skipping rental contract creation');
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error creating sublease:', err);
      setError(err instanceof Error ? err.message : 'Failed to create sublease');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto py-8">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white z-10">
          <h3 className="text-lg font-medium">Add New Sublease</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lot ID *
            </label>
            <div className="relative mt-1">
              <select
                value={formData.lotId}
                onChange={(e) => {
                  const selectedLotId = e.target.value;
                  console.log('Lot selected:', selectedLotId);

                  setFormData(prev => ({ ...prev, lotId: selectedLotId }));

                  if (validationErrors.lotId) {
                    setValidationErrors(prev => ({ ...prev, lotId: '' }));
                  }

                  // Fetch and set the permitted use value when a lot is selected
                  if (selectedLotId) {
                    // Clear the permitted use field first to avoid showing stale data
                    setFormData(prev => ({ ...prev, permittedUse: '' }));

                    // Fetch the permitted use for the selected lot
                    fetchPermittedUse(selectedLotId);
                  }
                }}
                className={`block w-full rounded-md border ${validationErrors.lotId ? 'border-red-500' : 'border-gray-300'
                  } px-3 py-2 focus:border-primary focus:ring-primary`}
                required
              >
                <option value="">Select a lot</option>
                {lots.map(lot => (
                  <option key={lot.lot_id} value={lot.lot_id}>
                    {lot.lot_id} {lot.community_name ? `(${lot.community_name})` : ''}
                  </option>
                ))}
              </select>

              {formData.lotId && (
                <div className="absolute inset-y-0 right-10 flex items-center pr-3 pointer-events-none">
                  {lots.find(lot => lot.lot_id === formData.lotId)?.lot_avail ? (
                    <div className="w-4 h-4 rounded-full bg-green-500" title="Lot is available"></div>
                  ) : (
                    <div className="w-4 h-4 rounded-full bg-red-500" title="Lot is not available"></div>
                  )}
                </div>
              )}
            </div>

            {validationErrors.lotId && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.lotId}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">Select the lot identifier</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Leaseholder Name
            </label>
            {/* Display loading indicator if leaseholders are loading */}
            {isLoadingLeaseholders && (
              <div className="mt-1 text-sm text-gray-500">Loading leaseholders...</div>
            )}

            {/* Display error message if there's an error */}
            {leaseholdersError && (
              <div className="mt-1 text-sm text-red-500">
                Error loading leaseholders: {leaseholdersError.message}
              </div>
            )}

            {/* Debug information removed for production */}

            <div className="relative">
              <select
                value={formData.holderId}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  const selectedLeaseholder = leaseholders?.find(l => l.holder_id.toString() === selectedId);

                  setFormData(prev => ({
                    ...prev,
                    holderId: selectedId,
                    // Keep holderName in sync for backward compatibility
                    holderName: selectedLeaseholder?.holder_name || ''
                  }));

                  if (validationErrors.holderName) {
                    setValidationErrors(prev => ({ ...prev, holderName: '' }));
                  }
                }}
                className={`mt-1 block w-full rounded-md border ${validationErrors.holderName ? 'border-red-500' : 'border-gray-300'
                  } px-3 py-2 focus:border-primary focus:ring-primary appearance-none bg-white cursor-pointer hover:border-primary`}
                disabled={isLoadingLeaseholders}
                style={{
                  backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")",
                  backgroundPosition: "right 0.5rem center",
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "1.5em 1.5em",
                  paddingRight: "2.5rem"
                }}
              >
                <option value="">Select a leaseholder</option>
                {leaseholders?.map((leaseholder) => (
                  <option
                    key={leaseholder.holder_id}
                    value={leaseholder.holder_id.toString()}
                    className="py-1"
                  >
                    {leaseholder.holder_name} (ID: {leaseholder.holder_id})
                  </option>
                ))}
              </select>
              {/* Add a visual indicator for the dropdown */}
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            {validationErrors.holderName && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.holderName}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">Select the leaseholder</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Start Date
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            />
            <p className="mt-1 text-xs text-gray-500">The date when the lease begins</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              End Date
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, endDate: e.target.value }));
                if (validationErrors.endDate) {
                  setValidationErrors(prev => ({ ...prev, endDate: '' }));
                }
              }}
              className={`mt-1 block w-full rounded-md border ${validationErrors.endDate ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary focus:ring-primary`}
            />
            {validationErrors.endDate && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.endDate}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">The date when the lease expires</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Permitted Use
            </label>
            <input
              type="text"
              value={formData.permittedUse.length > 50
                ? formData.permittedUse.substring(0, 50) + '...'
                : formData.permittedUse}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, permittedUse: e.target.value }));
                if (validationErrors.permittedUse) {
                  setValidationErrors(prev => ({ ...prev, permittedUse: '' }));
                }
              }}
              className={`mt-1 block w-full rounded-md border ${validationErrors.permittedUse ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary focus:ring-primary`}
              placeholder="Enter permitted use"
              title={formData.permittedUse}
            />
            {validationErrors.permittedUse && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.permittedUse}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.permittedUse.length > 50
                ? `Showing first 50 of ${formData.permittedUse.length} characters. Hover to see full text.`
                : 'Specify the allowed use for this lot. URLs will be automatically converted to clickable links when displayed.'}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Rental Amount
            </label>
            <input
              type="number"
              value={formData.rentalAmount}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, rentalAmount: e.target.value }));
                if (validationErrors.rentalAmount) {
                  setValidationErrors(prev => ({ ...prev, rentalAmount: '' }));
                }
              }}
              min="0"
              step="0.01"
              className={`mt-1 block w-full rounded-md border ${validationErrors.rentalAmount ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 focus:border-primary focus:ring-primary`}
              placeholder="Enter rental amount"
            />
            {validationErrors.rentalAmount && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.rentalAmount}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">The amount charged per rental interval</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Rental Interval
            </label>
            <select
              value={formData.reviewInterval}
              onChange={(e) => setFormData(prev => ({ ...prev, reviewInterval: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            >
              <option value="">Select interval</option>
              {REVIEW_INTERVALS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">How often the lease terms are reviewed</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lease Method
            </label>
            <select
              value={formData.leaseMethod}
              onChange={(e) => setFormData(prev => ({ ...prev, leaseMethod: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            >
              <option value="">Select method</option>
              {LEASE_METHODS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">How the lease was established</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lease Type
            </label>
            <select
              value={formData.leaseType}
              onChange={(e) => setFormData(prev => ({ ...prev, leaseType: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            >
              <option value="">Select type</option>
              {LEASE_TYPES.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">The category of this lease</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lease Dealing
            </label>
            <select
              value={formData.leaseDealing}
              onChange={(e) => setFormData(prev => ({ ...prev, leaseDealing: e.target.value }))}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary focus:ring-primary"
            >
              <option value="">Select dealing</option>
              {LEASE_DEALINGS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">The type of transaction for this lease</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lease Further Term
            </label>
            <input
              type="text"
              value={formData.leaseFurthTerm}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, leaseFurthTerm: e.target.value }));
                if (validationErrors.leaseFurthTerm) {
                  setValidationErrors(prev => ({ ...prev, leaseFurthTerm: '' }));
                }
              }}
              maxLength={30}
              className={`mt-1 block w-full rounded-md border ${validationErrors.leaseFurthTerm ? 'border-red-500' :
                formData.leaseFurthTerm.length > 25 ? 'border-amber-300' : 'border-gray-300'
                } px-3 py-2 focus:border-primary focus:ring-primary`}
              placeholder="Enter further term (max 30 chars)"
            />
            {validationErrors.leaseFurthTerm && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.leaseFurthTerm}
              </p>
            )}
            <div className="flex justify-between mt-1">
              <p className="text-xs text-gray-500">Specify any further term details</p>
              <p className={`text-xs ${formData.leaseFurthTerm.length > 25 ? 'text-amber-500 font-medium' : 'text-gray-500'
                }`}>
                {formData.leaseFurthTerm.length}/30
              </p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Lease Deal Document
            </label>
            <input
              type="text"
              value={formData.leaseDealDoc}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, leaseDealDoc: e.target.value }));
                if (validationErrors.leaseDealDoc) {
                  setValidationErrors(prev => ({ ...prev, leaseDealDoc: '' }));
                }
              }}
              maxLength={100}
              className={`mt-1 block w-full rounded-md border ${validationErrors.leaseDealDoc ? 'border-red-500' :
                formData.leaseDealDoc.length > 90 ? 'border-amber-300' : 'border-gray-300'
                } px-3 py-2 focus:border-primary focus:ring-primary`}
              placeholder="Enter deal document reference (max 100 chars)"
            />
            {validationErrors.leaseDealDoc && (
              <p className="mt-1 text-sm text-red-600 flex items-center">
                <AlertCircle className="h-4 w-4 mr-1" />
                {validationErrors.leaseDealDoc}
              </p>
            )}
            <div className="flex justify-between mt-1">
              <p className="text-xs text-gray-500">Reference to the deal document. URLs will be automatically converted to clickable links when displayed.</p>
              <p className={`text-xs ${formData.leaseDealDoc.length > 90 ? 'text-amber-500 font-medium' : 'text-gray-500'
                }`}>
                {formData.leaseDealDoc.length}/100
              </p>
            </div>
          </div>

          <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Sublease'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
